import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Chip,
  Card,
  CardContent,
  useTheme,
  alpha,
  Collapse,
  List,
  ListItem,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DragIndicator as DragIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';

interface Subtopic {
  name: string;
  keywords?: string[];
}

interface Category {
  name: string;
  definition?: string;
  description?: string; // Legacy field, use definition instead
  subtopics: Subtopic[];
  type?: 'intent' | 'thematic';
  weight?: number;
}

interface ConfigurationEditorProps {
  categories: Category[];
  onChange: (categories: Category[]) => void;
}

// Separate component for adding new categories to avoid re-renders
const AddCategoryForm: React.FC<{ onAddCategory: (category: Category) => void }> = React.memo(({ onAddCategory }) => {
  const theme = useTheme();
  const [newCategoryType, setNewCategoryType] = useState<'thematic' | 'intent'>('thematic');
  const [newCategoryDescription, setNewCategoryDescription] = useState<string>('');
  const newCategoryInputRef = React.useRef<HTMLInputElement>(null);

  const handleAddCategory = () => {
    const newName = newCategoryInputRef.current?.value;
    if (!newName || !newName.trim()) return;
    
    const newCategory: Category = {
      name: newName.trim(),
      subtopics: [],
      type: newCategoryType,
      weight: 50
    };
    
    // Add definition for intent categories (backend expects 'definition' field)
    if (newCategoryType === 'intent' && newCategoryDescription.trim()) {
      newCategory.definition = newCategoryDescription.trim();
    }
    
    onAddCategory(newCategory);
    
    // Reset form
    if (newCategoryInputRef.current) {
      newCategoryInputRef.current.value = '';
    }
    setNewCategoryType('thematic');
    setNewCategoryDescription('');
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.02)
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Enter new category name"
            inputRef={newCategoryInputRef}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newCategoryType !== 'intent') {
                handleAddCategory();
              }
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="category-type-label">Type</InputLabel>
            <Select
              labelId="category-type-label"
              value={newCategoryType}
              label="Type"
              onChange={(e) => setNewCategoryType(e.target.value as 'thematic' | 'intent')}
            >
              <MenuItem value="thematic">Thematic</MenuItem>
              <MenuItem value="intent">Intent</MenuItem>
            </Select>
          </FormControl>
          <Button
            sx={{ py: 0 }}
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddCategory}
          >
            Add
          </Button>
        </Box>
        
        {/* Show description field for intent categories */}
        {newCategoryType === 'intent' && (
          <TextField
            size="small"
            fullWidth
            placeholder="Enter description (optional)"
            value={newCategoryDescription}
            onChange={(e) => setNewCategoryDescription(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleAddCategory();
              }
            }}
            multiline
            rows={2}
          />
        )}
      </Box>
    </Paper>
  );
});

const ConfigurationEditor: React.FC<ConfigurationEditorProps> = React.memo(({ categories, onChange }) => {
  const theme = useTheme();
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [editingCategory, setEditingCategory] = useState<number | null>(null);
  const [editingSubtopic, setEditingSubtopic] = useState<{ categoryIdx: number; subtopicIdx: number } | null>(null);
  const [addingSubtopicTo, setAddingSubtopicTo] = useState<number | null>(null);
  const [editingWeight, setEditingWeight] = useState<{ categoryIdx: number; value: string } | null>(null);
  
  // Refs for uncontrolled inputs
  const categoryInputRef = React.useRef<HTMLInputElement>(null);
  const subtopicInputRef = React.useRef<HTMLInputElement>(null);
  const newSubtopicInputRef = React.useRef<HTMLInputElement>(null);

  // Clear editing weight state when categories change (e.g., after sorting)
  React.useEffect(() => {
    setEditingWeight(null);
  }, [categories]);

  // Toggle category expansion (only for thematic categories)
  const toggleCategory = (idx: number) => {
    // Don't allow toggling for intent categories
    if (categories[idx]?.type === 'intent') {
      return;
    }
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(idx)) {
      newExpanded.delete(idx);
    } else {
      newExpanded.add(idx);
    }
    setExpandedCategories(newExpanded);
  };

  // Add new category
  const handleAddCategory = (newCategory: Category) => {
    const newCategories = [...categories, newCategory];
    onChange(newCategories);
  };

  // Delete category
  const handleDeleteCategory = (idx: number) => {
    const newCategories = categories.filter((_, i) => i !== idx);
    onChange(newCategories);
  };

  // Update category name
  const handleUpdateCategoryName = (idx: number) => {
    const newName = categoryInputRef.current?.value;
    if (!newName || !newName.trim()) {
      setEditingCategory(null);
      return;
    }
    const newCategories = [...categories];
    newCategories[idx] = { ...newCategories[idx], name: newName.trim() };
    onChange(newCategories);
    setEditingCategory(null);
  };

  // Handle weight change (local state only, doesn't trigger re-sort)
  const handleWeightChange = (idx: number, value: string) => {
    setEditingWeight({ categoryIdx: idx, value });
  };

  // Commit weight change (triggers re-sort)
  const handleWeightCommit = (idx: number) => {
    if (!editingWeight || editingWeight.categoryIdx !== idx) {
      return;
    }
    
    const weight = parseInt(editingWeight.value, 10);
    // Validate weight is between 1-100
    if (isNaN(weight) || weight < 1 || weight > 100) {
      setEditingWeight(null);
      return;
    }
    
    const newCategories = [...categories];
    newCategories[idx] = { ...newCategories[idx], weight };
    onChange(newCategories);
    setEditingWeight(null);
  };

  // Add subtopic
  const handleAddSubtopic = (categoryIdx: number) => {
    const newName = newSubtopicInputRef.current?.value;
    if (!newName || !newName.trim()) return;
    const newCategories = [...categories];
    newCategories[categoryIdx] = {
      ...newCategories[categoryIdx],
      subtopics: [
        ...newCategories[categoryIdx].subtopics,
        { name: newName.trim() }
      ]
    };
    onChange(newCategories);
    if (newSubtopicInputRef.current) {
      newSubtopicInputRef.current.value = '';
    }
    setAddingSubtopicTo(null);
  };

  // Delete subtopic
  const handleDeleteSubtopic = (categoryIdx: number, subtopicIdx: number) => {
    const newCategories = [...categories];
    newCategories[categoryIdx] = {
      ...newCategories[categoryIdx],
      subtopics: newCategories[categoryIdx].subtopics.filter((_, i) => i !== subtopicIdx)
    };
    onChange(newCategories);
  };

  // Update subtopic name
  const handleUpdateSubtopicName = (categoryIdx: number, subtopicIdx: number) => {
    const newName = subtopicInputRef.current?.value;
    if (!newName || !newName.trim()) {
      setEditingSubtopic(null);
      return;
    }
    const newCategories = [...categories];
    newCategories[categoryIdx].subtopics[subtopicIdx] = {
      ...newCategories[categoryIdx].subtopics[subtopicIdx],
      name: newName.trim()
    };
    onChange(newCategories);
    setEditingSubtopic(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Category List */}
      {categories.map((category, categoryIdx) => (
        <Card
          key={categoryIdx}
          elevation={2}
          sx={{
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            '&:hover': {
              boxShadow: theme.shadows[4]
            }
          }}
        >
          {/* Category Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              cursor: category.type === 'intent' ? 'default' : 'pointer',
              '&:hover': {
                backgroundColor: category.type === 'intent' 
                  ? alpha(theme.palette.primary.main, 0.05)
                  : alpha(theme.palette.primary.main, 0.08)
              }
            }}
            onClick={() => toggleCategory(categoryIdx)}
          >
            <IconButton size="small" sx={{ mr: 1 }}>
              <DragIcon fontSize="small" />
            </IconButton>

            {editingCategory === categoryIdx ? (
              <TextField
                size="small"
                defaultValue={category.name}
                inputRef={categoryInputRef}
                autoFocus
                onClick={(e) => e.stopPropagation()}
                onBlur={() => handleUpdateCategoryName(categoryIdx)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleUpdateCategoryName(categoryIdx);
                  } else if (e.key === 'Escape') {
                    setEditingCategory(null);
                  }
                }}
                sx={{ flex: 1, mr: 1 }}
              />
            ) : (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    cursor: 'text',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    transition: 'all 0.2s ease',
                    border: '2px solid transparent',
                    '&:hover': {
                      border: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05)
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingCategory(categoryIdx);
                  }}
                >
                  {category.name}
                </Typography>
                {category.type && (
                  <Chip
                    label={category.type === 'intent' ? 'Intent' : 'Thematic'}
                    size="small"
                    sx={{
                      backgroundColor: category.type === 'intent' 
                        ? alpha(theme.palette.success.main, 0.15)
                        : alpha(theme.palette.warning.main, 0.15),
                      color: category.type === 'intent'
                        ? theme.palette.success.dark
                        : theme.palette.warning.dark,
                      fontWeight: 600,
                      borderColor: category.type === 'intent'
                        ? alpha(theme.palette.success.main, 0.5)
                        : alpha(theme.palette.warning.main, 0.5),
                      border: '1px solid'
                    }}
                  />
                )}
                {/* Only show subtopic count for thematic categories */}
                {category.type !== 'intent' && (
                  <Chip
                    label={`${category.subtopics.length} subtopics`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', mr: 2 }} onClick={(e) => e.stopPropagation()}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                    Weight:
                  </Typography>
                  <Tooltip 
                    title="Search queries may be related to more than one topic and subtopic, but those with higher weights will be prioritized as the primary assignment in the reporting"
                    placement="top"
                  >
                    <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                  <TextField
                    size="small"
                    type="number"
                    value={editingWeight?.categoryIdx === categoryIdx ? editingWeight.value : (category.weight || 50)}
                    onChange={(e) => handleWeightChange(categoryIdx, e.target.value)}
                    onBlur={() => handleWeightCommit(categoryIdx)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleWeightCommit(categoryIdx);
                      } else if (e.key === 'Escape') {
                        setEditingWeight(null);
                      }
                    }}
                    inputProps={{ 
                      min: 1, 
                      max: 100,
                      style: { textAlign: 'center', width: '60px' }
                    }}
                    sx={{ 
                      width: '80px',
                      '& .MuiInputBase-input': {
                        py: 0.5,
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
              {editingCategory === categoryIdx ? (
                <>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleUpdateCategoryName(categoryIdx)}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => setEditingCategory(null)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconButton
                    size="small"
                    onClick={() => setEditingCategory(categoryIdx)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteCategory(categoryIdx)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
              {/* Only show expand/collapse icon for thematic categories */}
              {category.type !== 'intent' && (
                <IconButton size="small">
                  {expandedCategories.has(categoryIdx) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Intent Category Description */}
          {category.type === 'intent' && (category.definition || category.description) && (
            <Box sx={{ px: 2, pb: 2, pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {category.definition || category.description}
              </Typography>
            </Box>
          )}

          {/* Category Content (Subtopics) - Only for thematic categories */}
          {category.type !== 'intent' && (
          <Collapse in={expandedCategories.has(categoryIdx)}>
            <CardContent sx={{ pt: 2 }}>
              {category.definition && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                  {category.definition}
                </Typography>
              )}

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Subtopics
                </Typography>
                <Tooltip title="Add subtopic" placement="top">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setAddingSubtopicTo(categoryIdx)}
                    sx={{ 
                      padding: '2px',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <List dense sx={{ mb: 1 }}>
                {category.subtopics.map((subtopic, subtopicIdx) => (
                  <ListItem
                    key={subtopicIdx}
                    sx={{
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: alpha(theme.palette.background.paper, 0.5),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.background.paper, 0.8)
                      }
                    }}
                  >
                    {editingSubtopic?.categoryIdx === categoryIdx && editingSubtopic?.subtopicIdx === subtopicIdx ? (
                      <TextField
                        size="small"
                        fullWidth
                        defaultValue={subtopic.name}
                        inputRef={subtopicInputRef}
                        autoFocus
                        onBlur={() => handleUpdateSubtopicName(categoryIdx, subtopicIdx)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleUpdateSubtopicName(categoryIdx, subtopicIdx);
                          } else if (e.key === 'Escape') {
                            setEditingSubtopic(null);
                          }
                        }}
                        sx={{ mr: 1 }}
                      />
                    ) : (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          flex: 1,
                          cursor: 'text',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          transition: 'all 0.2s ease',
                          border: '2px solid transparent',
                          '&:hover': {
                            border: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSubtopic({ categoryIdx, subtopicIdx });
                        }}
                      >
                        {subtopic.name}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {editingSubtopic?.categoryIdx === categoryIdx && editingSubtopic?.subtopicIdx === subtopicIdx ? (
                        <>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleUpdateSubtopicName(categoryIdx, subtopicIdx)}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => setEditingSubtopic(null)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => setEditingSubtopic({ categoryIdx, subtopicIdx })}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteSubtopic(categoryIdx, subtopicIdx)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>

              {/* Add Subtopic Input */}
              {addingSubtopicTo === categoryIdx && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Enter subtopic name"
                    inputRef={newSubtopicInputRef}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAddSubtopic(categoryIdx);
                      } else if (e.key === 'Escape') {
                        setAddingSubtopicTo(null);
                      }
                    }}
                  />
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleAddSubtopic(categoryIdx)}
                  >
                    <CheckIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setAddingSubtopicTo(null)}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              )}
            </CardContent>
          </Collapse>
          )}
        </Card>
      ))}

      {/* Add New Category */}
      <AddCategoryForm onAddCategory={handleAddCategory} />
    </Box>
  );
});

export default ConfigurationEditor;

